import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getCountryFlag } from "@/lib/countryFlags";
import { format, parseISO } from "date-fns";

function calcTimeLeft(target: Date) {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function Segment({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl font-black tabular-nums text-foreground sm:text-3xl">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
}

export default function CountdownTimer() {
  const { data: nextSession } = useQuery({
    queryKey: ["landing-next-race"],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data } = await supabase
        .from("sessions")
        .select("date_start, races!inner(id, meeting_name, country_name, status)")
        .eq("session_type", "Race")
        .neq("races.status", "cancelled")
        .gt("date_start", now)
        .order("date_start", { ascending: true })
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const nextRace = nextSession?.races && !Array.isArray(nextSession.races)
    ? nextSession.races
    : null;

  const target = useMemo(
    () => (nextSession?.date_start ? new Date(nextSession.date_start) : null),
    [nextSession?.date_start]
  );

  const [t, setT] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!target) return;
    setT(calcTimeLeft(target));
    const id = setInterval(() => setT(calcTimeLeft(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  const label = nextRace && nextSession
    ? `${format(parseISO(nextSession.date_start), "MMM d")} · ${nextRace.meeting_name} ${getCountryFlag(nextRace.country_name)}`
    : "Loading…";

  return (
    <div className="inline-flex items-center gap-1 rounded-xl border border-border bg-card/60 px-4 py-3 backdrop-blur-sm">
      <div className="mr-3 flex flex-col">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-primary">
          Lights Out
        </span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <Segment value={t.days} label="days" />
      <span className="text-lg font-bold text-muted-foreground">:</span>
      <Segment value={t.hours} label="hrs" />
      <span className="text-lg font-bold text-muted-foreground">:</span>
      <Segment value={t.minutes} label="min" />
      <span className="text-lg font-bold text-muted-foreground">:</span>
      <Segment value={t.seconds} label="sec" />
    </div>
  );
}
